import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useWizard } from 'use-wizard';

import { useAccount } from '@/hooks/useAccount';
import { queryParamsModule } from '@/modules/queryParams';
import { preloadImage } from '@/utils/preloadImage';

import { OnboardingProvider } from '../../context';

import { layoutDataDefault, layoutSteps, layoutStepsKeys } from './constants';
import { layoutQueryParams } from './params';

import type {
  LayoutDataKeyType,
  LayoutDataType,
  LayoutStepKeyType,
} from './types';

const LayoutReplikaRelationships: React.FC = () => {
  const { currentUser } = useAccount();

  const [step, wizard] = useWizard(layoutStepsKeys);

  const proxyWizard = React.useMemo(() => {
    const preloadStepImages = (stepIndex: number) => {
      const currStep = stepIndex + 1;
      const needToPreloadImages = layoutStepsKeys.length - currStep > 1;
      if (needToPreloadImages) {
        const stepPreloadName = layoutStepsKeys[currStep + 2] as
          | keyof typeof layoutSteps
          | undefined;

        if (stepPreloadName) {
          const images = layoutSteps[stepPreloadName].images;
          images.forEach((image) => preloadImage(image));
        }
      }
    };

    return new Proxy(wizard, {
      get(target, prop) {
        if (prop === 'goToStep') {
          preloadStepImages(target.stepIndex());

          return (step: string) => {
            queryParamsModule.setParam('step', step);
            target.goToStep(step);
          };
        }

        if (prop === 'nextStep') {
          preloadStepImages(target.stepIndex());

          return () => {
            const currentStepIndex = target.stepIndex();
            const nextStepKey = layoutStepsKeys[currentStepIndex + 1];

            if (nextStepKey) {
              target.nextStep();
              queryParamsModule.setParam('step', nextStepKey);
            }
          };
        }

        return Reflect.get(target, prop);
      },
    });
  }, [wizard]);

  React.useEffect(() => {
    if (currentUser.onboarded) {
      layoutQueryParams.removeStepsData();

      proxyWizard.goToStep('email');
    } else {
      const stepFromParams = queryParamsModule.getParam(
        'step',
      ) as LayoutStepKeyType;

      if (stepFromParams) {
        const stepIndex = layoutStepsKeys.indexOf(stepFromParams);
        if (stepIndex === 0) return;

        const prevSteps = layoutStepsKeys.slice(0, stepIndex);

        proxyWizard.history.push(...prevSteps, stepFromParams);
        proxyWizard.goToStep(stepFromParams);
      } else {
        proxyWizard.goToStep('preferences');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<LayoutDataType>({
    defaultValues: {
      name:
        queryParamsModule.getParam<LayoutDataKeyType>('name') ??
        layoutDataDefault.name,
      loneliness:
        queryParamsModule.getParam<LayoutDataKeyType>('loneliness') ??
        layoutDataDefault.loneliness,
      interests:
        queryParamsModule.getParamsList<LayoutDataKeyType>('interests') ??
        layoutDataDefault.interests,
      topics:
        queryParamsModule.getParam<LayoutDataKeyType>('topics') ??
        layoutDataDefault.topics,
      first_categories:
        queryParamsModule.getParamsList<LayoutDataKeyType>(
          'first_categories',
        ) ?? layoutDataDefault.first_categories,
      ai_characteristic:
        queryParamsModule.getParam<LayoutDataKeyType>('ai_characteristic') ??
        layoutDataDefault.ai_characteristic,
      categories:
        queryParamsModule.getParamsList<LayoutDataKeyType>('categories') ??
        layoutDataDefault.categories,
      interaction:
        queryParamsModule.getParam<LayoutDataKeyType>('interaction') ??
        layoutDataDefault.interaction,
      outfits:
        queryParamsModule.getParam<LayoutDataKeyType>('outfits') ??
        layoutDataDefault.outfits,
      preloadCategories: layoutDataDefault.preloadCategories,
      characters: layoutDataDefault.characters,
      email: layoutDataDefault.email,
    },
  });

  const StepComponent = layoutSteps[step as keyof typeof layoutSteps].component;

  return (
    <OnboardingProvider wizard={proxyWizard} steps={layoutSteps}>
      <FormProvider {...form}>
        <StepComponent />
      </FormProvider>
    </OnboardingProvider>
  );
};

export default LayoutReplikaRelationships;
